/**
 * WAHA API Service
 * Frontend API calls for WAHA WhatsApp integration
 */

import axios from 'axios';
import { installRestartRetry } from '../utils/restartRetry';
import { getLanguage } from '../utils/locale';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create axios instance with auth
const api = axios.create({
  baseURL: `${API_URL}/waha`,
  headers: {
    'Content-Type': 'application/json'
  }
});
installRestartRetry(api);

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['Accept-Language'] = getLanguage();
  return config;
});

// Convert error messages to user-friendly Turkish
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const serverMessage = error.response?.data?.message;

    if (!serverMessage) {
      const statusMessages: Record<number, string> = {
        400: i18n.t('Geçersiz istek. Lütfen bilgileri kontrol edin.'),
        403: i18n.t('Bu işlem için yetkiniz bulunmuyor.'),
        404: i18n.t('Aradığınız kayıt bulunamadı.'),
        408: i18n.t('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.'),
        429: i18n.t('Çok fazla istek gönderildi. Lütfen biraz bekleyin.'),
        500: i18n.t('Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.'),
        502: i18n.t('Sunucu geçici olarak kullanılamıyor. Sayfa yenilendiğinde düzelecektir.'),
        503: i18n.t('Servis şu anda bakımda. Lütfen birkaç dakika sonra tekrar deneyin.'),
        504: i18n.t('Sunucu yanıt vermedi. Lütfen tekrar deneyin.')
      };

      if (status && statusMessages[status]) {
        error.message = statusMessages[status];
      } else if (error.code === 'ERR_NETWORK') {
        error.message = i18n.t('İnternet bağlantınızı kontrol edin.');
      } else if (error.code === 'ECONNABORTED') {
        error.message = i18n.t('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.');
      }
    }

    return Promise.reject(error);
  }
);

// Types
export interface WAHASession {
  name: string;
  status: 'STARTING' | 'SCAN_QR_CODE' | 'WORKING' | 'STOPPED' | 'FAILED';
  me?: {
    id: string;
    pushName: string;
  };
}

export interface WAHAMessage {
  id: number;
  sessionName: string;
  chatId: string;
  messageId: string;
  type: string;
  content: string;
  direction: 'incoming' | 'outgoing';
  status: string;
  createdAt: string;
  // New fields for staff tracking
  readByUserId?: string;
  readByUserAt?: string;
  respondedByUserId?: string;
  respondedAt?: string;
  responseMessageId?: string;
  readByUser?: {
    id: string;
    username: string;
    company_name: string;
  };
  respondedByUser?: {
    id: string;
    username: string;
    company_name: string;
  };
  sender?: {
    id: string;
    username: string;
    company_name: string;
  };
}

export interface PersonelActivityReport {
  readStats: Array<{ readByUserId: string; readCount: number }>;
  respondedStats: Array<{ respondedByUserId: string; respondedCount: number }>;
  sentStats: Array<{ userId: string; sentCount: number }>;
  personelStats?: Array<{
    user_id: string;
    username: string;
    company_name: string;
    read_count: number;
    responded_count: number;
    sent_count: number;
  }>;
}

export interface WAHAChat {
  id: string;
  name: string;
  lastMessage?: string;
  timestamp?: string;
  unreadCount?: number;
  isGroup?: boolean;
}

export interface WAHAGroup {
  id: string;
  name: string;
  description?: string;
  participantsCount?: number;
}

export interface WAHAContact {
  id: string;
  name: string;
  pushname?: string;
  number: string;
  isMyContact?: boolean;
}

export interface BulkMessageResult {
  total: number;
  successful: number;
  failed: number;
  results: Array<{
    recipient: string;
    success: boolean;
    messageId?: string;
    error?: string;
    timestamp: string;
  }>;
}

// ==================== SESSION MANAGEMENT ====================

export interface MySession {
  id: number;
  sessionName: string;
  userId: string;
  status: 'STARTING' | 'SCAN_QR_CODE' | 'WORKING' | 'STOPPED' | 'FAILED';
  phoneNumber: string | null;
  phoneName: string | null;
  lastActivity: string | null;
  connectedAt: string | null;
  owner?: { id: string; username: string; company_name: string; role: string };
}

export const getMySessions = async (): Promise<{ sessions: MySession[]; canCreateSession: boolean }> => {
  const response = await api.get('/my-sessions');
  return { sessions: response.data.data, canCreateSession: response.data.canCreateSession ?? false };
};

export const createSession = async (): Promise<any> => {
  const response = await api.post('/sessions/create');
  return response.data;
};

export const deleteSession = async (sessionName: string): Promise<any> => {
  const response = await api.delete(`/sessions/${sessionName}`);
  return response.data;
};

export const getAllSessions = async (): Promise<WAHASession[]> => {
  const response = await api.get('/sessions');
  return response.data.data;
};

export const getSessionStatus = async (sessionName: string = 'default'): Promise<WAHASession> => {
  const response = await api.get(`/session/${sessionName}/status`);
  return response.data.data;
};

export const startSession = async (sessionName?: string, config?: object): Promise<any> => {
  const response = await api.post('/session/start', { sessionName, config });
  return response.data;
};

export const stopSession = async (sessionName: string = 'default'): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/stop`);
  return response.data;
};

export const logoutSession = async (sessionName: string = 'default'): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/logout`);
  return response.data;
};

export const getQRCode = async (sessionName: string = 'default'): Promise<{ qr: string; mimetype: string }> => {
  const response = await api.get(`/session/${sessionName}/qr`);
  return response.data.data;
};

export const getMe = async (sessionName: string = 'default'): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/me`);
  return response.data.data;
};

export const checkConnection = async (sessionName: string = 'default'): Promise<{
  connected: boolean;
  status: string;
  session?: any;
  error?: string;
}> => {
  const response = await api.get(`/session/${sessionName}/check`);
  return response.data.data;
};

// ==================== MESSAGING ====================

export const sendTextMessage = async (
  sessionName: string,
  chatId: string,
  text: string,
  replyTo?: string
): Promise<any> => {
  const payload: any = { chatId, text };
  if (replyTo) payload.reply_to = replyTo;
  const response = await api.post(`/session/${sessionName}/send/text`, payload);
  return response.data;
};

export const sendImageMessage = async (
  sessionName: string,
  chatId: string,
  imageUrl: string,
  caption?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/send/image`, { chatId, imageUrl, caption });
  return response.data;
};

export const sendFileMessage = async (
  sessionName: string,
  chatId: string,
  fileUrl: string,
  filename?: string,
  caption?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/send/file`, { chatId, fileUrl, filename, caption });
  return response.data;
};

export const sendVideoMessage = async (
  sessionName: string,
  chatId: string,
  videoUrl: string,
  caption?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/send/video`, { chatId, videoUrl, caption });
  return response.data;
};

export const sendVoiceMessage = async (
  sessionName: string,
  chatId: string,
  audioUrl: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/send/voice`, { chatId, audioUrl });
  return response.data;
};

export const sendLocationMessage = async (
  sessionName: string,
  chatId: string,
  latitude: number,
  longitude: number,
  title?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/send/location`, { chatId, latitude, longitude, title });
  return response.data;
};

export const sendBulkMessages = async (
  sessionName: string,
  recipients: string[],
  message: string,
  options?: {
    delay?: number;
    variables?: Record<string, string | string[]>;
  }
): Promise<BulkMessageResult> => {
  const response = await api.post(`/session/${sessionName}/send/bulk`, { recipients, message, options });
  return response.data.data;
};

// ==================== CHAT EXPORT ====================

export const exportChat = async (sessionName: string, chatId: string, format: 'csv' | 'txt', includeNotes: boolean = false): Promise<void> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/export`, {
    params: { format, includeNotes },
    responseType: 'blob'
  });
  const blob = new Blob([response.data], { type: format === 'csv' ? 'text/csv' : 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const chatName = chatId.replace('@c.us', '').replace('@lid', '').replace('@g.us', '');
  a.download = `sohbet_${chatName}_${new Date().toISOString().split('T')[0]}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
};

// ==================== MEDIA UPLOAD ====================

export const uploadMedia = async (sessionName: string, file: File): Promise<{ url: string; filename: string; originalName: string; mimetype: string; size: number }> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/session/${sessionName}/upload-media`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  return response.data.data;
};

export const sendMediaMessage = async (
  sessionName: string,
  chatId: string,
  mediaUrl: string,
  filename: string,
  mimetype: string,
  caption?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/send-media`, { chatId, mediaUrl, filename, mimetype, caption });
  return response.data;
};

// ==================== SCHEDULED MESSAGES ====================

export const scheduleMessage = async (
  sessionName: string,
  recipients: string[],
  message: string,
  scheduledAt: string,
  options?: any
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/schedule-message`, { recipients, message, scheduledAt, options });
  return response.data.data;
};

export const getScheduledMessages = async (sessionName: string): Promise<any[]> => {
  const response = await api.get(`/session/${sessionName}/scheduled-messages`);
  return response.data.data;
};

export const cancelScheduledMessage = async (sessionName: string, id: string): Promise<any> => {
  const response = await api.delete(`/session/${sessionName}/scheduled-messages/${id}`);
  return response.data;
};

export const deleteScheduledMessages = async (sessionName: string, ids?: string[]): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/scheduled-messages/delete-bulk`, { ids });
  return response.data;
};

// ==================== MESSAGE STATUS ====================

export const sendSeen = async (sessionName: string, chatId: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/seen`, { chatId });
  return response.data;
};

export const startTyping = async (sessionName: string, chatId: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/typing`, { chatId });
  return response.data;
};

export const setReaction = async (
  sessionName: string,
  chatId: string,
  messageId: string,
  reaction: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/reaction`, { chatId, messageId, reaction });
  return response.data;
};

// ==================== CHATS ====================

export const getChats = async (sessionName: string = 'default'): Promise<WAHAChat[]> => {
  const response = await api.get(`/session/${sessionName}/chats`);
  return response.data.data;
};

export const getChatMessages = async (
  sessionName: string,
  chatId: string,
  limit: number = 100
): Promise<WAHAMessage[]> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/messages`, {
    params: { limit }
  });
  return response.data.data;
};

// Fast message fetch from local DB (no WAHA API call)
export const getChatMessagesFromDB = async (
  sessionName: string,
  chatId: string,
  limit: number = 50,
  before?: string
): Promise<any[]> => {
  const params: any = { limit };
  if (before) params.before = before;
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/messages-db`, { params });
  return response.data;
};

export const deleteChat = async (sessionName: string, chatId: string): Promise<any> => {
  const response = await api.delete(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}`);
  return response.data;
};

export const archiveChat = async (sessionName: string, chatId: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/archive`);
  return response.data;
};

export const unarchiveChat = async (sessionName: string, chatId: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/unarchive`);
  return response.data;
};

export const deleteWhatsAppMessage = async (sessionName: string, chatId: string, messageId: string): Promise<any> => {
  const response = await api.delete(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/messages/${messageId}`);
  return response.data;
};

// ==================== TAGS ====================

export const getAllTags = async (sessionName: string): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/tags`);
  return response.data.data;
};

export const getMessageTags = async (sessionName: string, messageId: string): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/messages/${messageId}/tags`);
  return response.data.data;
};

export const addMessageTag = async (sessionName: string, messageId: string, tag: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/messages/${messageId}/tags`, { tag });
  return response.data;
};

export const removeMessageTag = async (sessionName: string, messageId: string, tag: string): Promise<any> => {
  const response = await api.delete(`/session/${sessionName}/messages/${messageId}/tags`, { data: { tag } });
  return response.data;
};

export const getMessagesByTag = async (sessionName: string, tag: string, page: number = 1, limit: number = 20): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/tags/messages`, { params: { tag, page, limit } });
  return response.data.data;
};

// ==================== GROUPS ====================

export const getGroups = async (sessionName: string = 'default'): Promise<WAHAGroup[]> => {
  const response = await api.get(`/session/${sessionName}/groups`);
  return response.data.data;
};

export const createGroup = async (
  sessionName: string,
  name: string,
  participants?: string[]
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/groups`, { name, participants });
  return response.data;
};

export const getGroup = async (sessionName: string, groupId: string): Promise<WAHAGroup> => {
  const response = await api.get(`/session/${sessionName}/groups/${groupId}`);
  return response.data.data;
};

export const getGroupParticipants = async (
  sessionName: string,
  groupId: string
): Promise<any[]> => {
  const response = await api.get(`/session/${sessionName}/groups/${groupId}/participants`);
  return response.data.data;
};

export const addGroupParticipants = async (
  sessionName: string,
  groupId: string,
  participants: string[]
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/groups/${groupId}/participants/add`, { participants });
  return response.data;
};

export const removeGroupParticipants = async (
  sessionName: string,
  groupId: string,
  participants: string[]
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/groups/${groupId}/participants/remove`, { participants });
  return response.data;
};

export const sendGroupMessage = async (
  sessionName: string,
  groupId: string,
  message: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/groups/${groupId}/send`, { message });
  return response.data;
};

export const setGroupSubject = async (sessionName: string, groupId: string, subject: string): Promise<any> => {
  const response = await api.put(`/session/${sessionName}/groups/${encodeURIComponent(groupId)}/subject`, { subject });
  return response.data;
};

export const setGroupDescription = async (sessionName: string, groupId: string, description: string): Promise<any> => {
  const response = await api.put(`/session/${sessionName}/groups/${encodeURIComponent(groupId)}/description`, { description });
  return response.data;
};

export const leaveGroup = async (sessionName: string, groupId: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/groups/${encodeURIComponent(groupId)}/leave`);
  return response.data;
};

// ==================== CONTACTS ====================

export const getContacts = async (sessionName: string = 'default'): Promise<WAHAContact[]> => {
  const response = await api.get(`/session/${sessionName}/contacts`);
  return response.data.data;
};

export const getContact = async (sessionName: string, contactId: string): Promise<WAHAContact> => {
  const response = await api.get(`/session/${sessionName}/contacts/${encodeURIComponent(contactId)}`);
  return response.data.data;
};

export const checkNumberExists = async (
  sessionName: string,
  phoneNumber: string
): Promise<{ exists: boolean; chatId?: string }> => {
  const response = await api.post(`/session/${sessionName}/contacts/check`, { phoneNumber });
  return response.data.data;
};

export const blockContact = async (sessionName: string, contactId: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/contacts/${encodeURIComponent(contactId)}/block`);
  return response.data;
};

export const unblockContact = async (sessionName: string, contactId: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/contacts/${encodeURIComponent(contactId)}/unblock`);
  return response.data;
};

// ==================== PROFILE PICTURES ====================

export const getContactProfilePicture = async (
  sessionName: string,
  contactId: string
): Promise<{ profilePictureURL: string | null }> => {
  const response = await api.get(`/session/${sessionName}/contacts/${encodeURIComponent(contactId)}/profile-picture`);
  return response.data.data;
};

export const getBulkProfilePictures = async (
  sessionName: string,
  contactIds: string[]
): Promise<Record<string, string | null>> => {
  const response = await api.post(`/session/${sessionName}/contacts/profile-pictures`, { contactIds });
  return response.data.data;
};

// Profil resmi cache (session storage kullan)
const profilePictureCache: Record<string, { url: string | null; timestamp: number }> = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika

export const getProfilePictureWithCache = async (
  sessionName: string,
  contactId: string
): Promise<string | null> => {
  const cacheKey = `${sessionName}:${contactId}`;
  const cached = profilePictureCache[cacheKey];

  // Use cached value if available and not expired
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.url;
  }

  try {
    const result = await getContactProfilePicture(sessionName, contactId);
    profilePictureCache[cacheKey] = {
      url: result.profilePictureURL,
      timestamp: Date.now()
    };
    return result.profilePictureURL;
  } catch {
    return null;
  }
};

// ==================== STATISTICS ====================

export const getStats = async (sessionName: string = 'default'): Promise<{
  totalMessages: number;
  sentMessages: number;
  receivedMessages: number;
}> => {
  const response = await api.get(`/session/${sessionName}/stats`);
  return response.data.data;
};

export const getMessageHistory = async (
  sessionName: string,
  options?: {
    chatId?: string;
    page?: number;
    limit?: number;
  }
): Promise<{
  messages: WAHAMessage[];
  total: number;
  page: number;
  totalPages: number;
}> => {
  const response = await api.get(`/session/${sessionName}/history`, { params: options });
  return response.data.data;
};

// ==================== MESSAGE SEARCH ====================

export const searchMessages = async (
  sessionName: string,
  query: string,
  options?: { page?: number; limit?: number }
): Promise<{
  results: Array<{ id: number; chat_id: string; content: string; sender_name: string; direction: string; type: string; created_at: string; message_id: string }>;
  total: number;
  page: number;
  totalPages: number;
}> => {
  const response = await api.get(`/session/${sessionName}/search-messages`, { params: { q: query, ...options } });
  return response.data.data;
};

export const getMessagesAround = async (
  sessionName: string,
  messageId: string,
  count: number = 30
): Promise<{ messages: any[]; targetMessageId: string }> => {
  const response = await api.get(`/session/${sessionName}/messages-around/${encodeURIComponent(messageId)}`, { params: { count } });
  return response.data.data;
};

// ==================== PERSONEL TRACKING ====================

export const markMessageRead = async (
  sessionName: string,
  messageId: string
): Promise<WAHAMessage> => {
  const response = await api.post(`/session/${sessionName}/messages/${encodeURIComponent(messageId)}/mark-read`);
  return response.data.data;
};

export const markMessageResponded = async (
  sessionName: string,
  messageId: string,
  responseMessageId?: string
): Promise<WAHAMessage> => {
  const response = await api.post(`/session/${sessionName}/messages/${encodeURIComponent(messageId)}/mark-responded`, {
    responseMessageId
  });
  return response.data.data;
};

export const getChatMessagesWithPersonelInfo = async (
  sessionName: string,
  chatId: string,
  limit: number = 100
): Promise<WAHAMessage[]> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/messages-with-personel`, {
    params: { limit }
  });
  return response.data.data;
};

export const getPersonelActivityReport = async (
  sessionName: string,
  options?: {
    startDate?: string;
    endDate?: string;
    userId?: string;
  }
): Promise<PersonelActivityReport> => {
  const response = await api.get(`/session/${sessionName}/personel-activity`, { params: options });
  return response.data.data;
};

// ==================== UTILITIES ====================

export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  let cleaned = phone.replace(/\D/g, '');

  // Handle Turkish numbers
  if (cleaned.startsWith('0')) {
    cleaned = '90' + cleaned.substring(1);
  } else if (!cleaned.startsWith('90') && cleaned.length === 10) {
    cleaned = '90' + cleaned;
  }

  return cleaned;
};

// ==================== TEAM STATISTICS ====================

export const getTeamStats = async (sessionName: string): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/team-stats`);
  return response.data;
};

// ==================== INTERNAL NOTES ====================

export const addInternalNote = async (
  sessionName: string,
  chatId: string,
  content: string,
  visibility: 'private' | 'team' = 'team',
  quotedMessage?: { content: string; sender: string; timestamp: string }
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/internal-note`, { content, visibility, quotedMessage });
  return response.data;
};

export const updateInternalNote = async (
  sessionName: string,
  noteId: string,
  data: { content?: string; visibility?: 'private' | 'team' }
): Promise<any> => {
  const response = await api.put(`/session/${sessionName}/notes/${noteId}`, data);
  return response.data;
};

export const deleteInternalNote = async (
  sessionName: string,
  noteId: string
): Promise<any> => {
  const response = await api.delete(`/session/${sessionName}/notes/${noteId}`);
  return response.data;
};

export const getChatNotes = async (
  sessionName: string,
  chatId: string
): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/notes`);
  return response.data;
};

export const formatChatId = (phone: string): string => {
  const cleaned = formatPhoneNumber(phone);
  return `${cleaned}@c.us`;
};

// ==================== SOHBET ATAMA ====================

export const claimChat = async (
  sessionName: string,
  chatId: string,
  chatName?: string,
  priority?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/claim`, { chatName, priority });
  return response.data;
};

export const assignChat = async (
  sessionName: string,
  chatId: string,
  assignTo: string,
  chatName?: string,
  priority?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/assign`, { assignTo, chatName, priority });
  return response.data;
};

export const unassignChat = async (
  sessionName: string,
  chatId: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/unassign`);
  return response.data;
};

export const getChatAssignment = async (
  sessionName: string,
  chatId: string
): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/assignment`);
  return response.data;
};

export const assignChatToDepartment = async (
  sessionName: string,
  chatId: string,
  departmentId: string,
  chatName?: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/assign-department`, { departmentId, chatName });
  return response.data;
};

export const unassignChatDepartment = async (
  sessionName: string,
  chatId: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/unassign-department`);
  return response.data;
};

export const getMyMentions = async (sessionName: string): Promise<any> => {
  const response = await api.get(`/mentions/my`);
  return response.data;
};

export const markMentionRead = async (sessionName: string, mentionId: string): Promise<any> => {
  const response = await api.put(`/mentions/${mentionId}/read`);
  return response.data;
};

export const checkMentionAccess = async (sessionName: string, chatId: string): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/mention-access`);
  return response.data;
};

export const transferChatDepartment = async (
  sessionName: string,
  chatId: string,
  toDepartmentId: string
): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/transfer-department`, { toDepartmentId });
  return response.data;
};

// Participants
export const getParticipants = async (sessionName: string, chatId: string): Promise<any> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/participants`);
  return response.data;
};

export const addParticipant = async (sessionName: string, chatId: string, userId: string, role: 'responsible' | 'participant' = 'participant'): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/participants`, { userId, role });
  return response.data;
};

export const removeParticipant = async (sessionName: string, chatId: string, userId: string): Promise<any> => {
  const response = await api.delete(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/participants/${userId}`);
  return response.data;
};

export const changeParticipantRole = async (sessionName: string, chatId: string, userId: string, role: 'responsible' | 'participant'): Promise<any> => {
  const response = await api.put(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/participants/${userId}/role`, { role });
  return response.data;
};

// Team Chat
export const sendTeamMessage = async (sessionName: string, chatId: string, content: string): Promise<any> => {
  const response = await api.post(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/team-message`, { content });
  return response.data;
};

// ==================== WHATSAPP LABELS ====================

export const getWhatsAppLabels = async (sessionName: string): Promise<any[]> => {
  const response = await api.get(`/session/${sessionName}/labels`);
  return response.data.data;
};

export const getLabelChats = async (sessionName: string, labelId: string): Promise<any[]> => {
  const response = await api.get(`/session/${sessionName}/labels/${labelId}/chats`);
  return response.data.data;
};

export const getChatWhatsAppLabels = async (sessionName: string, chatId: string): Promise<any[]> => {
  const response = await api.get(`/session/${sessionName}/chats/${encodeURIComponent(chatId)}/labels`);
  return response.data.data;
};

// ==================== CHANNELS ====================

export const getChannels = async (sessionName: string): Promise<any[]> => {
  const response = await api.get(`/session/${sessionName}/channels`);
  return response.data.data;
};

export default {
  // Sessions
  getAllSessions,
  getSessionStatus,
  startSession,
  stopSession,
  logoutSession,
  getQRCode,
  getMe,
  checkConnection,

  // Messaging
  sendTextMessage,
  sendImageMessage,
  sendFileMessage,
  sendVideoMessage,
  sendVoiceMessage,
  sendLocationMessage,
  sendBulkMessages,

  // Message Status
  sendSeen,
  startTyping,
  setReaction,

  // Chats
  getChats,
  getChatMessages,
  deleteChat,
  archiveChat,

  // Groups
  getGroups,
  createGroup,
  getGroup,
  getGroupParticipants,
  addGroupParticipants,
  removeGroupParticipants,
  sendGroupMessage,

  // Contacts
  getContacts,
  getContact,
  checkNumberExists,
  blockContact,
  unblockContact,

  // Profile Pictures
  getContactProfilePicture,
  getBulkProfilePictures,
  getProfilePictureWithCache,

  // Stats
  getStats,
  getMessageHistory,

  // Personel Tracking
  markMessageRead,
  markMessageResponded,
  getChatMessagesWithPersonelInfo,
  getPersonelActivityReport,

  // Utilities
  formatPhoneNumber,
  formatChatId
};
